import React, { useState, useEffect } from "react";
import { Modal, ProgressBar } from "react-bootstrap";
import {
  FiX,
  FiUploadCloud,
  FiTrash2,
  FiCheck,
  FiFileText,
  FiImage,
  FiVideo,
  FiArrowRight,
  FiArrowLeft,
} from "react-icons/fi";
import { Sparkles } from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { showToast } from "../../../components/common/notification/Notification";
import { resolveAssetUrl, formatCurrency } from "../utils/formatters";
import "./PromotionCreateModal.css";

const initialForm = {
  project_name: "",
  project_description: "",
  starting_cost: "",
  type_id: "",
  state_name: "",
  city_name: "",
  country_name: "India",
  area_name: "",
  pin_code: "",
  promotion_url: "",
  images: [],
  attachements: [],
  promotion_videos: [],
};

const PromotionCreateModal = ({
  show,
  onHide,
  promotionService,
  onPromotionCreated,
}) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  // States, Cities, Types dropdown data
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);

  // AI Description Generator state
  const [aiPrompt, setAiPrompt] = useState("");
  const [generatingAi, setGeneratingAi] = useState(false);
  const [showAiInput, setShowAiInput] = useState(false);

  // Uploading state
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(false);

  useEffect(() => {
    if (show) {
      setStep(1);
      setFormData(initialForm);
      loadDropdowns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const loadDropdowns = async () => {
    try {
      const [fetchedStates, fetchedTypes] = await Promise.all([
        promotionService.fetchStates(1),
        promotionService.fetchPropertyTypes(),
      ]);
      setStates(fetchedStates || []);
      setPropertyTypes(fetchedTypes || []);
    } catch (err) {
      console.error("Failed to load states/types", err);
    }
  };

  const handleStateChange = async (selectedStateName) => {
    setFormData((prev) => ({
      ...prev,
      state_name: selectedStateName,
      city_name: "",
    }));

    const stateObj = states.find((s) => s.name === selectedStateName);
    if (stateObj?.id) {
      try {
        const fetchedCities = await promotionService.fetchCities(stateObj.id);
        setCities(fetchedCities || []);
      } catch (err) {
        console.error("Failed to fetch cities", err);
      }
    } else {
      setCities([]);
    }
  };

  // AI Description Generation (API 9)
  const handleGenerateAiDescription = async () => {
    const input =
      aiPrompt.trim() ||
      `${formData.project_name || "Luxury residential property"} in ${
        formData.area_name || formData.city_name || "prime location"
      } starting at ${formData.starting_cost || "attractive pricing"} with premium modern amenities.`;

    try {
      setGeneratingAi(true);
      const data = await promotionService.generateAiDescription(input);
      const generatedText =
        data?.description || data?.generated_text || data?.data || data?.text || "";

      if (generatedText) {
        setFormData((prev) => ({
          ...prev,
          project_description: generatedText,
        }));
        showToast("AI generated description successfully!", { type: "success" });
      } else {
        showToast("No description returned by AI service", { type: "error" });
      }
    } catch (err) {
      console.error("AI Generation error:", err);
      showToast("Failed to generate AI description", { type: "error" });
    } finally {
      setGeneratingAi(false);
    }
  };

  // Media Upload (Images & Videos - API 7)
  const handleMediaUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    try {
      setUploadingMedia(true);
      const newImages = [];
      const newVideos = [];

      for (const file of files) {
        if (file.size > 20 * 1024 * 1024) {
          showToast(`${file.name} is too large (max 20MB)`, { type: "error" });
          continue;
        }

        const res = await promotionService.uploadMedia(file);
        const uploadedList = res?.propertyImages || res?.data || [];

        uploadedList.forEach((uploaded) => {
          const isVideo =
            file.type.startsWith("video/") ||
            (uploaded.imagePath && uploaded.imagePath.endsWith(".mp4"));

          if (isVideo) {
            newVideos.push({
              name: uploaded.name || file.name,
              url_path: uploaded.image_url || uploaded.imagePath,
              description: "Video",
            });
          } else {
            newImages.push({
              imagePath: uploaded.imagePath || uploaded.image_url,
              name: uploaded.name || file.name,
              description: "Promotion Image",
              image_url: uploaded.image_url || uploaded.imagePath,
              type: "image",
            });
          }
        });
      }

      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...newImages],
        promotion_videos: [...prev.promotion_videos, ...newVideos],
      }));
      showToast("Media uploaded successfully", { type: "success" });
    } catch (err) {
      console.error("Media upload error:", err);
      showToast("Failed to upload media files", { type: "error" });
    } finally {
      setUploadingMedia(false);
      e.target.value = null;
    }
  };

  // Attachment / Document Upload (API 8)
  const handleAttachmentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    try {
      setUploadingDocs(true);
      const newAttachments = [];

      for (const file of files) {
        if (file.size > 20 * 1024 * 1024) {
          showToast(`${file.name} is too large (max 20MB)`, { type: "error" });
          continue;
        }

        const res = await promotionService.uploadAttachments(file);
        const uploadedDocs = res?.propertyAttachments || res?.data || [];

        uploadedDocs.forEach((doc, idx) => {
          newAttachments.push({
            attachmentPath: doc.attachmentPath || doc.attachment_url || file.name,
            name: doc.name || file.name || `attachment_${idx + 1}`,
            description: "Promotion Document",
            attachment_url: doc.attachment_url || doc.attachmentPath,
          });
        });
      }

      setFormData((prev) => ({
        ...prev,
        attachements: [...prev.attachements, ...newAttachments],
      }));
      showToast("Attachments uploaded successfully", { type: "success" });
    } catch (err) {
      console.error("Document upload error:", err);
      showToast("Failed to upload attachment", { type: "error" });
    } finally {
      setUploadingDocs(false);
      e.target.value = null;
    }
  };

  // Submit Promotion (API 5)
  const handleSubmit = async () => {
    if (!formData.project_name.trim()) {
      showToast("Project Name is required", { type: "error" });
      setStep(1);
      return;
    }
    if (!formData.starting_cost) {
      showToast("Starting Cost is required", { type: "error" });
      setStep(1);
      return;
    }
    if (!formData.state_name || !formData.city_name) {
      showToast("State and City are required", { type: "error" });
      setStep(1);
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        project_name: formData.project_name.trim(),
        project_description: formData.project_description,
        starting_cost: String(formData.starting_cost),
        type_id: Number(formData.type_id) || 1,
        city_name: formData.city_name,
        state_name: formData.state_name,
        country_name: formData.country_name || "India",
        promotion_url: formData.promotion_url.trim(),
        area_name: formData.area_name.trim(),
        pin_code: formData.pin_code.trim(),
        images: formData.images.map((img) => ({
          imagePath: img.imagePath || img.image_url,
          name: img.name || "image.jpg",
          description: img.description || "Promotion Image",
          image_url: img.image_url || img.imagePath,
          type: "image",
        })),
        attachements: formData.attachements.map((att) => ({
          attachmentPath: att.attachmentPath || att.attachment_url,
          name: att.name || "brochure.pdf",
          description: att.description || "Promotion Brochure",
          attachment_url: att.attachment_url || att.attachmentPath,
        })),
        promotion_videos: formData.promotion_videos.map((vid) => ({
          name: vid.name || "video.mp4",
          url_path: vid.url_path,
          description: vid.description || "Promotion Video",
        })),
      };

      const res = await promotionService.create(payload);
      if (res?.statusType === "SUCCESS" || res?.id || res?.status === 200) {
        showToast("Promotion created successfully!", { type: "success" });
        if (onPromotionCreated) onPromotionCreated();
        onHide();
      } else {
        showToast(res?.message || "Failed to create promotion", { type: "error" });
      }
    } catch (err) {
      console.error("Create promotion error:", err);
      showToast("Failed to create promotion. Please try again.", { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="promo-create-modal">
      <Modal.Header className="promo-create-modal__header">
        <div className="promo-create-modal__title-wrap">
          <span className="promo-create-modal__eyebrow">New Campaign</span>
          <h3 className="promo-create-modal__title">Create Property Promotion</h3>
        </div>
        <button
          type="button"
          className="promo-create-modal__close-btn"
          onClick={onHide}
          aria-label="Close"
        >
          <FiX size={20} />
        </button>
      </Modal.Header>

      {/* Progress step bar */}
      <div className="promo-create-modal__steps">
        <div className="promo-create-modal__step-labels">
          <span className={step === 1 ? "active" : ""}>1. Details</span>
          <span className={step === 2 ? "active" : ""}>2. Media & Docs</span>
          <span className={step === 3 ? "active" : ""}>3. Review</span>
        </div>
        <ProgressBar now={(step / 3) * 100} className="promo-progress-bar" />
      </div>

      <Modal.Body className="promo-create-modal__body">
        {/* STEP 1: Details */}
        {step === 1 && (
          <div className="promo-form-step">
            <div className="row g-3">
              {/* Project Name */}
              <div className="col-12">
                <label className="promo-form-label">
                  Project / Promotion Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="promo-form-input"
                  placeholder="e.g. Luxury Residency Skyline Towers"
                  value={formData.project_name}
                  onChange={(e) =>
                    setFormData({ ...formData, project_name: e.target.value })
                  }
                />
              </div>

              {/* Property Type & Starting Cost */}
              <div className="col-md-6">
                <label className="promo-form-label">
                  Property Type <span className="text-danger">*</span>
                </label>
                <select
                  className="promo-form-select"
                  value={formData.type_id}
                  onChange={(e) =>
                    setFormData({ ...formData, type_id: e.target.value })
                  }
                >
                  <option value="">Select Property Type</option>
                  {propertyTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="promo-form-label">
                  Starting Cost (₹) <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="promo-form-input"
                  placeholder="e.g. 5000000 or ₹50 Lakh"
                  value={formData.starting_cost}
                  onChange={(e) =>
                    setFormData({ ...formData, starting_cost: e.target.value })
                  }
                />
              </div>

              {/* State & City */}
              <div className="col-md-6">
                <label className="promo-form-label">
                  State <span className="text-danger">*</span>
                </label>
                <select
                  className="promo-form-select"
                  value={formData.state_name}
                  onChange={(e) => handleStateChange(e.target.value)}
                >
                  <option value="">Select State</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="promo-form-label">
                  City <span className="text-danger">*</span>
                </label>
                <select
                  className="promo-form-select"
                  value={formData.city_name}
                  onChange={(e) =>
                    setFormData({ ...formData, city_name: e.target.value })
                  }
                >
                  <option value="">Select City</option>
                  {cities.map((c) => (
                    <option key={c.id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Locality & PIN Code */}
              <div className="col-md-6">
                <label className="promo-form-label">Area / Locality</label>
                <input
                  type="text"
                  className="promo-form-input"
                  placeholder="e.g. Koregaon Park / Whitefield"
                  value={formData.area_name}
                  onChange={(e) =>
                    setFormData({ ...formData, area_name: e.target.value })
                  }
                />
              </div>

              <div className="col-md-6">
                <label className="promo-form-label">PIN Code</label>
                <input
                  type="text"
                  className="promo-form-input"
                  placeholder="e.g. 411001"
                  value={formData.pin_code}
                  onChange={(e) =>
                    setFormData({ ...formData, pin_code: e.target.value })
                  }
                />
              </div>

              {/* Promotion External URL */}
              <div className="col-12">
                <label className="promo-form-label">Promotion / Project URL</label>
                <input
                  type="url"
                  className="promo-form-input"
                  placeholder="https://example.com/project-showcase"
                  value={formData.promotion_url}
                  onChange={(e) =>
                    setFormData({ ...formData, promotion_url: e.target.value })
                  }
                />
              </div>

              {/* AI Description Generator */}
              <div className="col-12">
                <div className="promo-ai-generator-bar">
                  <div className="promo-ai-generator-header">
                    <span className="promo-ai-tag">
                      <Sparkles size={14} /> AI Assistant
                    </span>
                    <button
                      type="button"
                      className="promo-ai-toggle-btn"
                      onClick={() => setShowAiInput(!showAiInput)}
                    >
                      {showAiInput ? "Close AI Prompt" : "✨ Write with AI"}
                    </button>
                  </div>

                  {showAiInput && (
                    <div className="promo-ai-input-wrap">
                      <input
                        type="text"
                        className="promo-ai-prompt-input"
                        placeholder="Enter keywords (e.g. Exclusive 2 & 3 BHK luxury flats with infinity pool)"
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                      />
                      <button
                        type="button"
                        className="promo-ai-generate-btn"
                        onClick={handleGenerateAiDescription}
                        disabled={generatingAi}
                      >
                        {generatingAi ? (
                          <span className="spinner-border spinner-border-sm me-1" />
                        ) : (
                          <Sparkles size={14} className="me-1" />
                        )}
                        Generate
                      </button>
                    </div>
                  )}
                </div>

                <label className="promo-form-label mt-2">
                  Project Description
                </label>
                <div className="promo-quill-wrap">
                  <ReactQuill
                    theme="snow"
                    value={formData.project_description}
                    onChange={(val) =>
                      setFormData({ ...formData, project_description: val })
                    }
                    placeholder="Provide highlights, amenities, floor plans, and promoter details..."
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: Media & Documents */}
        {step === 2 && (
          <div className="promo-form-step">
            {/* Image / Video Upload */}
            <div className="promo-upload-card mb-4">
              <div className="promo-upload-card__header">
                <div>
                  <h5 className="promo-upload-card__title">
                    <FiImage /> Property Media (Images & Videos)
                  </h5>
                  <p className="promo-upload-card__sub">
                    Upload high-res JPG, PNG, or MP4 promotion videos.
                  </p>
                </div>
                <label className="promo-file-picker-btn">
                  <FiUploadCloud size={16} />
                  <span>Choose Files</span>
                  <input
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    className="d-none"
                    onChange={handleMediaUpload}
                    disabled={uploadingMedia}
                  />
                </label>
              </div>

              {uploadingMedia && (
                <div className="promo-upload-progress">
                  <span className="spinner-border spinner-border-sm text-primary me-2" />
                  <span>Uploading media to server...</span>
                </div>
              )}

              {formData.images.length === 0 && formData.promotion_videos.length === 0 ? (
                <div className="promo-upload-empty">No media uploaded yet.</div>
              ) : (
                <div className="promo-media-grid">
                  {formData.images.map((img, idx) => (
                    <div key={idx} className="promo-uploaded-thumb">
                      <img
                        src={resolveAssetUrl(img.image_url || img.imagePath)}
                        alt={img.name}
                      />
                      <button
                        type="button"
                        className="promo-uploaded-thumb__del"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            images: formData.images.filter((_, i) => i !== idx),
                          })
                        }
                      >
                        <FiTrash2 size={12} />
                      </button>
                    </div>
                  ))}

                  {formData.promotion_videos.map((vid, idx) => (
                    <div key={`vid-${idx}`} className="promo-uploaded-thumb promo-uploaded-thumb--video">
                      <FiVideo size={24} className="text-white" />
                      <span className="promo-uploaded-thumb__name">{vid.name}</span>
                      <button
                        type="button"
                        className="promo-uploaded-thumb__del"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            promotion_videos: formData.promotion_videos.filter(
                              (_, i) => i !== idx
                            ),
                          })
                        }
                      >
                        <FiTrash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Document / Brochure Upload */}
            <div className="promo-upload-card">
              <div className="promo-upload-card__header">
                <div>
                  <h5 className="promo-upload-card__title">
                    <FiFileText /> Documents & PDF Brochures
                  </h5>
                  <p className="promo-upload-card__sub">
                    Upload official project brochures, floor plans, and specification sheets.
                  </p>
                </div>
                <label className="promo-file-picker-btn">
                  <FiUploadCloud size={16} />
                  <span>Choose PDFs</span>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    multiple
                    className="d-none"
                    onChange={handleAttachmentUpload}
                    disabled={uploadingDocs}
                  />
                </label>
              </div>

              {uploadingDocs && (
                <div className="promo-upload-progress">
                  <span className="spinner-border spinner-border-sm text-primary me-2" />
                  <span>Uploading documents...</span>
                </div>
              )}

              {formData.attachements.length === 0 ? (
                <div className="promo-upload-empty">No attachments uploaded yet.</div>
              ) : (
                <div className="promo-docs-list">
                  {formData.attachements.map((att, idx) => (
                    <div key={idx} className="promo-doc-row">
                      <div className="promo-doc-row__left">
                        <FiFileText className="text-danger" size={18} />
                        <span className="promo-doc-row__name">{att.name}</span>
                      </div>
                      <button
                        type="button"
                        className="promo-doc-row__del"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            attachements: formData.attachements.filter(
                              (_, i) => i !== idx
                            ),
                          })
                        }
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP 3: Review */}
        {step === 3 && (
          <div className="promo-form-step">
            <div className="promo-review-summary">
              <h4 className="promo-review-summary__title">
                {formData.project_name || "Untitled Promotion"}
              </h4>
              <p className="promo-review-summary__loc">
                {[formData.area_name, formData.city_name, formData.state_name]
                  .filter(Boolean)
                  .join(", ")}
              </p>

              <div className="promo-review-grid">
                <div className="promo-review-item">
                  <span className="label">Starting Price</span>
                  <span className="value">{formatCurrency(formData.starting_cost)}</span>
                </div>
                <div className="promo-review-item">
                  <span className="label">Property Type</span>
                  <span className="value">
                    {propertyTypes.find((t) => String(t.id) === String(formData.type_id))
                      ?.name || "Standard"}
                  </span>
                </div>
                <div className="promo-review-item">
                  <span className="label">Uploaded Images</span>
                  <span className="value">{formData.images.length} files</span>
                </div>
                <div className="promo-review-item">
                  <span className="label">Attachments</span>
                  <span className="value">{formData.attachements.length} files</span>
                </div>
              </div>

              {formData.promotion_url && (
                <div className="promo-review-url mt-3">
                  <strong>Website URL:</strong> {formData.promotion_url}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="promo-create-modal__footer">
        {step > 1 && (
          <button
            type="button"
            className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
            onClick={() => setStep(step - 1)}
            disabled={submitting}
          >
            <FiArrowLeft /> Back
          </button>
        )}

        {step < 3 ? (
          <button
            type="button"
            className="btn btn-primary d-inline-flex align-items-center gap-2"
            onClick={() => {
              if (step === 1 && !formData.project_name) {
                showToast("Please enter a Project Name", { type: "error" });
                return;
              }
              setStep(step + 1);
            }}
          >
            Continue <FiArrowRight />
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-success d-inline-flex align-items-center gap-2 px-4"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm" />
                Publishing...
              </>
            ) : (
              <>
                <FiCheck /> Publish Promotion
              </>
            )}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default PromotionCreateModal;
